import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EligibilityDiscoveryGuardDto, NphiesEligibilityResultDto } from '@his/shared';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { Dialog, DataTable } from '@/components/ui/layout';
import { Alert, Badge, Button, Field, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { useBranchPayers } from './branch-payers';

export function PolicySearchDialog({
  patientId,
  payerId: fixedPayerId,
  payerIdsWithPolicy,
  onClose,
  onDone,
}: {
  patientId: string;
  payerId?: string;
  payerIdsWithPolicy: readonly string[];
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [chosenPayerId, setChosenPayerId] = useState(fixedPayerId ?? '');
  const [result, setResult] = useState<NphiesEligibilityResultDto | null>(null);
  const payers = useBranchPayers(!fixedPayerId);
  const payerId = fixedPayerId ?? chosenPayerId;
  const noPolicyOnFile = !payerIdsWithPolicy.includes(payerId);

  const guard = useQuery({
    queryKey: ['eligibility-discovery-guard', patientId, payerId, noPolicyOnFile],
    queryFn: () => get<EligibilityDiscoveryGuardDto>(`/insurance/eligibility/discovery-guard${qs({ patientId, payerId, hasInsuranceButNoPolicy: noPolicyOnFile })}`),
    enabled: Boolean(payerId),
    staleTime: 15_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['patient-coverages', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
    void queryClient.invalidateQueries({ queryKey: ['eligibility-sessions'] });
    void queryClient.invalidateQueries({ queryKey: ['authorisation'] });
  };

  const search = useMutation({
    mutationFn: () => post<NphiesEligibilityResultDto>('/integrations/nphies/eligibility/discovery', { patientId, payerId, hasInsuranceButNoPolicy: noPolicyOnFile }),
    onSuccess: (answer) => {
      setResult(answer);
      refresh();
    },
    onError: (caught) => toast.error(tr('billing', 'nx_policy_search_failed'), describeError(caught)),
  });

  const confirm = useMutation({
    mutationFn: (coverageId: string) => post(`/insurance/coverages/${coverageId}/confirm`, {}),
    onSuccess: () => {
      toast.success(tr('billing', 'nx_policy_added'));
      refresh();
      onDone();
    },
    onError: (caught) => toast.error(tr('billing', 'nx_could_not_use_policy'), describeError(caught)),
  });

  const allowed = guard.data?.allowed === true;

  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={tr('billing', 'nx_policy_search_title')}
      description={tr('billing', 'nx_policy_search_description')}
      footer={
        <Button variant="ghost" onClick={onClose}>
          {tr('billing', 'close')}
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          {!fixedPayerId && (
            <Field label={tr('billing', 'payer')}>
              <Select value={chosenPayerId} onChange={(event) => setChosenPayerId(event.target.value)}>
                <option value="">{tr('billing', 'choose')}…</option>
                {payers.data?.map((payer) => (
                  <option key={payer.id} value={payer.id}>
                    {payer.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Button loading={search.isPending} disabled={!payerId || !allowed || !can('nphies.eligibility')} onClick={() => search.mutate()}>
            {tr('billing', 'nx_search_policies')}
          </Button>
        </div>
        {payerId && guard.data && !guard.data.allowed && <Alert variant="warning">{guard.data.reason ?? tr('billing', 'nx_policy_search_not_allowed')}</Alert>}
        {payerId && guard.isError && <Alert variant="destructive">{describeError(guard.error)}</Alert>}
        {result && (
          <Alert variant={result.policies.length > 0 ? 'default' : 'destructive'}>
            {result.policies.length > 0 ? tr('billing', 'nx_policies_found', { count: result.policies.length }) : result.message}
          </Alert>
        )}
        {result?.warnings.map((warning) => (
          <Alert key={warning} variant="warning">
            {warning}
          </Alert>
        ))}
        {result && result.policies.length > 0 && (
          <DataTable
            dense
            rows={result.policies}
            rowKey={(policy) => policy.coverageId}
            columns={[
              { header: tr('billing', 'payer'), cell: (policy) => policy.payerName },
              { header: tr('billing', 'plan'), cell: (policy) => policy.planName ?? '—' },
              { header: tr('billing', 'nx_member'), cell: (policy) => <span className="font-mono text-xs">{policy.memberId ?? '—'}</span> },
              {
                header: tr('billing', 'status'),
                cell: (policy) =>
                  policy.inForce === false ? <Badge variant="destructive">{tr('billing', 'nx_eligibility_not_in_force')}</Badge> : policy.planMatched ? <Badge variant="success">{tr('billing', 'nx_in_force')}</Badge> : <Badge variant="warning">{tr('billing', 'nx_plan_not_matched')}</Badge>,
              },
              {
                header: '',
                align: 'right',
                cell: (policy) =>
                  policy.proposed && can('coverage.verify') ? (
                    <Button size="sm" loading={confirm.isPending && confirm.variables === policy.coverageId} onClick={() => confirm.mutate(policy.coverageId)}>
                      {tr('billing', 'nx_use_this_policy')}
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">{policy.proposed ? tr('billing', 'nx_needs_confirming_by_authorised_staff') : tr('billing', 'nx_already_on_file')}</span>
                  ),
              },
            ]}
          />
        )}
      </div>
    </Dialog>
  );
}
